import { EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { ComponentViewModelInterface } from '@nts/std/interfaces';
import { UUIDHelper } from '@nts/std/utility';
import { PropertyViewModelPropertyChangedEventArgs } from './property-view-property-changed-event-args';

export class ComponentViewModelUtility {
  static getRootViewModel<TViewModel>(viewModel: { parent?: any; }): TViewModel {
    let current: any = viewModel;

    while (current?.parent != null) {
      current = current.parent;
    }

    return current as TViewModel;
  }

  static getComponentById<TComponent extends ComponentViewModelInterface>(
    components: Map<string, TComponent>,
    componentId: string,
    factory: () => TComponent = () => new ComponentViewModel() as unknown as TComponent
  ): TComponent {
    let componentVm = components?.get(componentId);

    if (componentVm == null) {
      componentVm = factory();
      components?.set(componentId, componentVm);
    }

    return componentVm;
  }

  static setIsVisible<TComponent extends ComponentViewModelInterface>(
    components: Map<string, TComponent>,
    componentId: string,
    value: boolean
  ): TComponent {
    const componentVm = this.getComponentById(components, componentId);
    componentVm.isVisible = value;
    return componentVm;
  }
}

export class ComponentViewModel implements ComponentViewModelInterface {
  propertyChanged: EventEmitter<PropertyViewModelPropertyChangedEventArgs> = new EventEmitter();
  readonly uniqueId = UUIDHelper.generateUUID();

  protected internalIsVisible = true;

  get isVisible() {
    return this.internalIsVisible;
  }

  set isVisible(value: boolean) {
    if (this.internalIsVisible !== value) {
      this.internalIsVisible = value;
      this.onPropertyChanged('isVisible');
      this.isVisible$.next(value);
    }
  }

  isVisible$ = new BehaviorSubject<boolean>(this.internalIsVisible);

  onPropertyChanged(propertyName: string = null) {
    const args = new PropertyViewModelPropertyChangedEventArgs();
    args.propertyName = propertyName;
    this.propertyChanged.emit(args);
  }
}
